<template>
  <el-dialog
    custom-class="MyDialog"
    :title="dialogTitle"
    :visible.sync="visible"
    width="500px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div class="confirm-content">
      <div class="confirm-text" v-html="confirmText"></div>
      <div v-if="showNote" class="confirm-note">
        <div class="note-title">{{ $t('app.auto_277_6784f2d4') }}</div>
        <div class="note-item">{{ $t('app.auto_278_3c291386') }}</div>
        <div class="note-item">{{ $t('app.auto_279_9d974559') }}</div>
      </div>
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose" :disabled="confirmLoading">{{ $t('app.cancel') }}</el-button>
      <el-button type="primary" @click="handleConfirm" :loading="confirmLoading">{{ $t('app.confirm') }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'RemoveStudentConfirmDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    // 移除类型：'single' 单个, 'batch' 批量
    removeType: {
      type: String,
      default: 'single'
    },
    // 单个学生数据
    studentData: {
      type: Object,
      default: () => ({})
    },
    // 批量移除的学生列表
    studentList: {
      type: Array,
      default: () => []
    },
    // 当前督学师名称
    currentSupervisorName: {
      type: String,
      default: ''
    },
    // 学生是否有多个督学师（单个移除时使用）
    hasMultipleSupervisors: {
      type: Boolean,
      default: false
    },
    // 当前督学状态名称（单个移除时使用）
    supervisorStatusName: {
      type: String,
      default: ''
    }
  },
  computed: {
    dialogTitle() {
      if (this.removeType === 'single') {
        // 判断是正式还是体验学生
        const isTrial = this.isTrialStudent(this.studentData);
        return isTrial ? this.$t('app.auto_398_5ad9a814') : this.$t('app.auto_399_0204a557');
      } else {
        // 批量移除
        const hasTrial = this.studentList.some(s => this.isTrialStudent(s));
        const hasFormal = this.studentList.some(s => !this.isTrialStudent(s));
        if (hasTrial && hasFormal) {
          return this.$t('app.auto_400_4e3209c7');
        } else if (hasTrial) {
          return this.$t('app.auto_401_da364415');
        } else {
          return this.$t('app.auto_402_78775655');
        }
      }
    },
    confirmText() {
      const supervisorName = this.currentSupervisorName || this.$t('app.auto_403_94fa966b');
      
      if (this.removeType === 'single') {
        // 单个移除
        const isTrial = this.isTrialStudent(this.studentData);
        const studentName = this.studentData.studentName || '';
        
        if (isTrial) {
          // 场景3：移除单个体验学生
          return `<span style="color: #4a83e8">${studentName}</span>将从「${supervisorName}负责的学生」中移除，请确认`;
        } else {
          // 正式学生
          if (this.hasMultipleSupervisors) {
            // 场景2：学生不止一个督学师
            return `<span style="color: #4a83e8">${studentName}</span>将从「${supervisorName}负责的学生」中移除。该学生还有其他负责的分析师，督学状态保持不变，仍然是：${this.supervisorStatusName || this.$t('app.auto_404_83e062bf')}。请确认`;
          } else {
            // 场景1：学生只有当前督学师
            return `<span style="color: #4a83e8">${studentName}</span>将从「${supervisorName}负责的学生」中移除，督学状态改为：不督学。请确认`;
          }
        }
      } else {
        // 批量移除
        const formalCount = this.studentList.filter(s => !this.isTrialStudent(s)).length;
        const trialCount = this.studentList.filter(s => this.isTrialStudent(s)).length;
        
        if (formalCount > 0 && trialCount > 0) {
          // 场景4：有正式和体验学生
          return `「${supervisorName}负责的学生」中将移除<span style="color: #4a83e8">${formalCount}</span>{{ $t('app.auto_280_267d7726') }}<span style="color: #4a83e8">${trialCount}</span>个体验学员。请确认。`;
        } else if (formalCount > 0) {
          // 场景5：只有正式学生
          return `「${supervisorName}负责的学生」中将移除<span style="color: #4a83e8">${formalCount}</span>个正式学员，请确认。`;
        } else {
          // 场景6：只有体验学生
          return `「${supervisorName}负责的学生」中将移除<span style="color: #4a83e8">${trialCount}</span>个体验学员，请确认。`;
        }
      }
    },
    showNote() {
      // 批量移除正式学生时显示注意事项
      if (this.removeType === 'batch') {
        const hasFormal = this.studentList.some(s => !this.isTrialStudent(s));
        return hasFormal;
      }
      return false;
    }
  },
  data() {
    return {
      confirmLoading: false
    };
  },
  watch: {
    visible(newVal) {
      // 弹框关闭时重置 loading 状态
      if (!newVal) {
        this.confirmLoading = false;
      }
    }
  },
  methods: {
    // 判断是否是体验学生
    // 根据实际数据结构调整，可能需要通过 accountType 或其他字段判断
    isTrialStudent(student) {
      if (!student) return false;
      return student.accountType != 1 
    },
    handleClose() {
      this.$emit('close');
    },
    handleConfirm() {
      // 如果正在提交中，直接返回，防止重复点击
      if (this.confirmLoading) {
        return;
      }
      this.confirmLoading = true;
      this.$emit('confirm');
    }
  }
};
</script>

<style lang="scss" scoped>
.confirm-content {
  padding: 20px 0;
  
  .confirm-text {
    font-size: 14px;
    color: #303133;
    line-height: 24px;
    margin-bottom: 16px;
  }
  
  .confirm-note {
    margin-top: 16px;
    padding: 12px;
    background-color: #f5f7fa;
    border-radius: 4px;
    
    .note-title {
      font-size: 14px;
      font-weight: 500;
      color: #303133;
      margin-bottom: 8px;
    }
    
    .note-item {
      font-size: 13px;
      color: #606266;
      line-height: 20px;
      margin-bottom: 4px;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.dialog-footer {
  text-align: right;
}
</style>
